import React, { useCallback, useState, useEffect } from 'react';
import { IEditorProps } from './Editor';
import { IPropertyChild, getChildValueAsString } from 'components/property-editors/utils/ProviderNodeUtils';
import { getEditorConfigValue, showLabelAtTop } from 'components/property-editors/utils/EditorConfigurationUtils';
import { Checkbox } from '../inputs/Checkbox';
import { useEditorId } from './hooks/useEditorId';
import { EditorLabel } from './EditorLabel';
import { EditorValidationWrapper } from './EditorValidationWrapper';
import './RadioListEditor.less';

interface IRadioListEditorProps extends IEditorProps {}

/**
 * Simple Radio List
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function RadioListEditor({ child, id, path, onChange }: IRadioListEditorProps) {
  [id] = useEditorId(path, id);
  const [selected, setSelected] = useState(getChildValueAsString(child));
  const changeHandler = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSelected(value);
      onChange(value, path);
    },
    [onChange, path]
  );

  useEffect(() => {
    setSelected(getChildValueAsString(child));
  }, [child]);

  const radios = getRadioOptions(child).map(value => (
    <Checkbox
      className="dpc-radio-list-editor__option"
      key={value}
      id={`${id}-${value}`}
      label={value}
      value={value}
      type="radio"
      onChange={changeHandler}
      checked={selected === value}
    />
  ));

  return (
    <EditorLabel
      editor={child}
      path={path}
      htmlFor={id}
      data-testid={id}
      hideInputLabel={!showLabelAtTop(child)}
      className="dpc-radio-list-editor"
      doNotWrap
    >
      <EditorValidationWrapper editor={child}>
        <div className="dpc-radio-list">{radios}</div>
      </EditorValidationWrapper>
    </EditorLabel>
  );
}

function getRadioOptions(child: IPropertyChild): string[] {
  return getEditorConfigValue(child, 'values');
}
